import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';

import { routes } from 'src/app/constants';
import { Board, BoardColumn, BoardRow, SavedReport } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { BoardsService } from 'src/services/boards.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  selector: 'app-report-builder',
  templateUrl: './report-builder.component.html',
  styleUrls: ['./report-builder.component.scss'],
})
export class ReportBuilderComponent implements OnInit {
  rows: BoardRow[] = [];
  selectedRow: BoardRow | null = null;
  boardId: string;
  board?: Board;
  newColumnCount: number = 2;
  reportName: string = '';
  isReadOnly: boolean = false;
  isTvMode: boolean = false;
  savedReportLookup: Record<string, SavedReport> = {};
  isLoading = false;

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private navController: NavController,
    private boardsService: BoardsService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private msgSrvc: MessageService,
    private alertController: AlertController,
  ) {}

  async ngOnInit() {
    await this.getSavedReports();
    this.addRow();

    this.route.params.subscribe((params) => {
      this.boardId = params['boardId'];

      if (this.boardId) {
        this.getBoard();
      }
    });

    // Detect if the user hits the "esc" key and exit tv mode
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isTvMode) {
        this.toggleTvMode();
      }
    });
  }

  async getBoard() {
    this.isLoading = true;
    this.board = await this.boardsService.getById(this.boardId);

    if (this.board) {
      this.rows = this.board.rows;
      this.reportName = this.board.name;
    }

    this.isReadOnly = true;
    this.isLoading = false;
  }

  async getSavedReports() {
    this.isLoading = true;

    const savedReports: SavedReport[] = await this.apiService.getPromise('/saved-reports');

    if (savedReports.length) {
      savedReports.forEach((savedReport) => {
        this.savedReportLookup[savedReport.id] = savedReport;
      });
    }

    this.isLoading = false;
  }

  addRow() {
    const newRow: BoardRow = {
      id: 'row_' + Date.now(),
      columns: [],
    };

    // Add default columns based on newColumnCount
    for (let i = 0; i < this.newColumnCount; i++) {
      newRow.columns.push({
        id: 'col_' + Date.now() + '_' + i,
        content: 'Column ' + (i + 1),
        width: 100 / this.newColumnCount,
        title: 'Column ' + (i + 1),
      });
    }

    this.rows.push(newRow);
  }

  deleteRow(rowIndex: number) {
    this.rows.splice(rowIndex, 1);
    if (this.rows.length === 0) {
      this.selectedRow = null;
    }
  }

  selectRow(row: BoardRow) {
    this.selectedRow = row;
  }

  addColumn(row: BoardRow) {
    const columnCount = row.columns.length;
    const newWidth = 100 / (columnCount + 1);

    // Adjust width of existing columns
    row.columns.forEach((col) => {
      col.width = newWidth;
    });

    // Add new column
    row.columns.push({
      id: 'col_' + Date.now(),
      content: 'New Column',
      width: newWidth,
      title: 'New Column',
    });
  }

  deleteColumn(row: BoardRow, colIndex: number) {
    row.columns.splice(colIndex, 1);

    // Redistribute widths
    const columnCount = row.columns.length;
    if (columnCount > 0) {
      const newWidth = 100 / columnCount;
      row.columns.forEach((col) => {
        col.width = newWidth;
      });
    }
  }

  // Handle row drag and drop
  dropRow(event: CdkDragDrop<BoardRow[]>) {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
  }

  // Handle column drag and drop within a row
  dropColumn(event: CdkDragDrop<BoardColumn[]>, row: BoardRow) {
    if (event.previousContainer === event.container) {
      // Move within the same row
      moveItemInArray(row.columns, event.previousIndex, event.currentIndex);
    } else {
      // Move between rows
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

      // Recalculate column widths for both rows
      const sourceRow = this.rows.find((r) => r.columns === event.previousContainer.data);
      const targetRow = row;

      if (sourceRow && sourceRow.columns.length > 0) {
        const newSourceWidth = 100 / sourceRow.columns.length;
        sourceRow.columns.forEach((col) => (col.width = newSourceWidth));
      }

      if (targetRow.columns.length > 0) {
        const newTargetWidth = 100 / targetRow.columns.length;
        targetRow.columns.forEach((col) => (col.width = newTargetWidth));
      }
    }
  }

  // Update column widths when resizing
  updateColumnWidths(row: BoardRow) {
    const totalColumns = row.columns.length;
    const equalWidth = 100 / totalColumns;
    row.columns.forEach((col) => {
      col.width = equalWidth;
    });
  }

  // Export layout as JSON
  exportLayout() {
    return JSON.stringify(this.rows, null, 2);
  }

  // Get all connected column lists for drag and drop between rows
  getConnectedLists(): string[] {
    return this.rows.map((row) => 'columns-' + row.id);
  }

  // Helper method to check if editing is allowed
  canEdit(): boolean {
    return !this.isReadOnly;
  }

  // Toggle between edit and read-only modes
  toggleEditMode(): void {
    this.isReadOnly = !this.isReadOnly;
    if (this.isReadOnly) {
      this.selectedRow = null; // Deselect row when entering read-only mode
    }
  }

  toggleTvMode(): void {
    this.isTvMode = !this.isTvMode;
    this.navController.navigateRoot([], {
      queryParams: {
        tvMode: this.isTvMode,
      },
      queryParamsHandling: 'merge',
    });
  }

  onNameChange() {
    if (this.boardId) {
      this.save();
    }
  }

  async save() {
    this.isReadOnly = true;

    if (!this.reportName) {
      this.reportName = 'Untitled Board';
    }

    const isNewBoard = !this.boardId;

    this.isLoading = true;

    const update: Partial<Board> = {
      name: this.reportName,
      rows: this.rows,
    };

    try {
      if (isNewBoard) {
        this.board = await this.boardsService.create(update);
        this.boardId = this.board.id;
      } else {
        await this.boardsService.update(this.boardId, update);
      }

      document.dispatchEvent(new CustomEvent('saved-board', { detail: this.board }));

      if (isNewBoard) {
        setTimeout(() => {
          this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/boards/${this.boardId}`);
        }, 300);
      }
    } catch (error) {
      this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async deleteBoard() {
    const alert = await this.alertController.create({
      header: 'Delete Board',
      message: 'Are you sure you want to delete this board?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            // User canceled the deletion
          },
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            // Proceed with deletion
            this.isLoading = true;
            await this.boardsService.delete(this.boardId);
            this.isLoading = false;
            this.navController.navigateRoot([routes.DASHBOARD, routes.ANALYTICS]);
          },
        },
      ],
    });

    await alert.present();
  }

  copyLink() {
    const link = `${window.location.origin}/analytics/boards/${this.boardId}`;
    navigator.clipboard.writeText(link);
    this.msgSrvc.show('Link copied to clipboard');
  }

  chooseSavedReport(column: BoardColumn) {
    this.searchablePopoverService.savedReports({
      event,
      selectedReportId: column.id,
      callback: (id: string, name: string, object: any) => {
        column.savedReportId = id;
      },
    });
  }

  viewReport(column: BoardColumn) {
    this.navController.navigateRoot([
      routes.DASHBOARD,
      routes.ANALYTICS,
      'performance',
      'reports',
      column.savedReportId,
    ]);
  }

  openReportInNewTab(reportId: string): void {
    window.open(`/dashboard/analytics/performance/reports/${reportId}`, '_blank', 'noopener');
  }
}
